import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@mondivago/supabase';

// ─── Footer sections ───

interface FooterLinkItem {
  label: string;
  to: string;
}

interface FooterSection {
  title: string;
  links: FooterLinkItem[];
}

const FOOTER_SECTIONS: FooterSection[] = [
  {
    title: 'Suppliers',
    links: [
      { label: 'Supplier Login', to: '/supplier/login' },
      { label: 'List Your Tour', to: '/supplier/register' },
      { label: 'Partner Login', to: '/supplier/login?type=partner' },
      { label: 'Become a Partner', to: '/supplier/register?type=partner' },
    ],
  },
  {
    title: 'Explore',
    links: [
      { label: 'Home', to: '/' },
      { label: 'Search', to: '/search' },
      { label: 'Explore', to: '/explore' },
      { label: 'Destinations', to: '/destinations' },
      { label: 'Attractions', to: '/attractions' },
      { label: 'Blog', to: '/blog' },
    ],
  },
  {
    title: 'How it works',
    links: [
      { label: 'Work with us', to: '/work-with-us' },
      { label: 'For Suppliers', to: '/for-suppliers' },
      { label: 'For Partners', to: '/for-partners' },
      { label: 'About Us', to: '/about-us' },
    ],
  },
  {
    title: 'Support',
    links: [
      { label: 'Contact', to: '/contact' },
      { label: 'Help Center', to: '/help-center' },
      { label: 'System Status', to: '/status' },
    ],
  },
];

export function Footer() {
  const { t } = useTranslation();
  const { user } = useAuth();

  return (
    <footer className="bg-white border-t border-gray-200 mt-24 pb-20 md:pb-0">
      <div className="mx-auto max-w-6xl px-4 lg:px-0 py-16">
        <div className="grid grid-cols-2 gap-x-6 gap-y-10 md:grid-cols-[1.5fr_1fr_1fr_1fr_1fr]">
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="text-3xl font-black tracking-tight flex items-center mb-4">
              <span className="text-slate-950">Mondi</span>
              <span className="text-lime-500">vago</span>
            </Link>
            <p className="max-w-sm text-sm leading-relaxed text-gray-500 font-medium">
              {t(
                'footer.desc',
                'Discover and book unforgettable tours, activities, and local experiences around the world with verified suppliers. Your next adventure starts here.'
              )}
            </p>
          </div>

          {FOOTER_SECTIONS.map((section) => {
            const sectionKey = `footer.sections.${section.title.toLowerCase().replace(/\s+/g, '')}`;
            return (
              <div key={section.title}>
                <h4 className="eyebrow !text-xs text-slate-950 mb-6">{t(sectionKey, section.title)}</h4>
                <ul className="space-y-4 desc-md">
                  {section.links.map((link) => {
                    const linkKey = `footer.links.${link.label.toLowerCase().replace(/\s+/g, '')}`;
                    return (
                      <li key={link.to}>
                        <Link
                          to={link.label === 'Supplier Login' && user ? '/supplier/dashboard' : link.to}
                          className="transition-colors duration-300 hover:text-lime-600"
                        >
                          {t(linkKey, link.label)}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>

        <div className="mt-16 border-t border-gray-200 pt-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <p className="meta-upper text-gray-400">
            {t('footer.allRightsReserved', '© {{year}} Mondivago. All rights reserved.', {
              year: new Date().getFullYear(),
            })}
          </p>
          <div className="flex flex-wrap items-center gap-3 sm:gap-4 meta-upper text-gray-400">
            <Link to="/legal" className="hover:text-gray-900 transition-colors">
              {t('footer.legal', 'Legal')}
            </Link>
            <span className="hidden sm:inline">•</span>
            <Link to="/legal/privacy" className="hover:text-gray-900 transition-colors">
              {t('footer.privacy', 'Privacy')}
            </Link>
            <span className="hidden sm:inline">•</span>
            <Link to="/legal/terms" className="hover:text-gray-900 transition-colors">
              {t('footer.terms', 'Terms')}
            </Link>
            <span className="hidden sm:inline">•</span>
            <Link to="/legal/cookies" className="hover:text-gray-900 transition-colors">
              {t('footer.cookie', 'Cookie')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
